import {createSlice, createAsyncThunk, createSelector} from "@reduxjs/toolkit"
import isURL from "validator/lib/isURL"
import axios from "axios"
import { fetchUniqueCounts, fetchUniqueCountsForEachPath } from "./workspaceNoteSlice"
import {resetWorkingArticleNoteRelation, setWorkingAttachmentRelation} from "./workingArticleNoteRelationSlice"
import { attachArticlesToNote } from "./breadcrumbSlice"
import error from "eslint-plugin-react/lib/util/error"
import {formatDateWithYear} from "../utils/dates"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

/***************
 * CONST VALUES *
 ***************/
/**
 * a client-side implementation of the AttachmentDto object
 * everything except "title", "description", and "type" are optional values
 * @type {AttachmentDto}
 */
const defaultAttachmentObject = {
	title:"",
	description: "",
	url:"",
	attachmentType: null,
	fileName: "",
	fileType: "",
	s3Url: "",
}

export const NOTE_ATTACHMENT_TYPES = {
	url: "url",
	file: "file",
	doc: "doc"
}

export const postNoteAttachment = createAsyncThunk(
	"noteAttachmentSlice/postNoteAttachment",
	async ({attachment, note}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const {workspaceId} = note
		const userId = state.user?.value?.id
		const noteId = note.id

		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}
	
		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment`
			// we only need to encode data as formData if we're uploading a file...
			const {data} = attachment.attachmentType === "file" ? await axios.postForm(url, attachment) : await axios.post(url, attachment)
			dispatch(fetchUniqueCounts({id: noteId, workspaceId}))
			return {attachment: data, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const postNoteFileAttachment = createAsyncThunk(
	"noteAttachmentSlice/postNoteFileAttachment",
	async ({attachment, note}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const {workspaceId} = note
		const userId = state.user?.value?.id
		const noteId = note.id

		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try{
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/file/attachment`
			const {data} = await axios.postForm(url, attachment)
			dispatch(fetchUniqueCounts({id: noteId, workspaceId}))
			return {attachment: data, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const patchNoteFileAttachment = createAsyncThunk(
	"noteAttachmentSlice/patchNoteAttachment",
	async ({attachment, note, attachmentId}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const { workspaceId } = note
		const userId = state.user?.value?.id
		const noteId = note.id

		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try{
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/file/attachment/${attachmentId}`
			const {data} = await axios.patch(url, attachment, {
				headers: {"Content-Type": "multipart/form-data"}
			})
			dispatch(await fetchUniqueCounts({id: note.id, workspaceId}))
			return {attachment: data, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const fetchUsableDownloadLinkForAttachmentFile = createAsyncThunk (
	"noteAttachmentSlice/fetchUsableDownloadLinkForAttachmentFile",
	async ({attachmentId, workspaceId}, {getState, rejectWithValue}) => {
		try {
			const state = getState()
			const userId = state.user?.value?.id

			const {data: downloadUrl} = await axios.get(`/api/workspace/${workspaceId}/user/${userId}/attachment/${attachmentId}/download-link`)

			return downloadUrl
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const editNoteAttachment = createAsyncThunk(
	"noteAttachmentSlice/editNoteAttachment",
	async ({attachment, note}, {getState, rejectWithValue}) => {
		const state = getState()
		const { workspaceId } = note
		const userId = state.user?.value?.id
		const noteId = note.id
		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}
		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment/${attachment.id}`
			const {data} = await axios.put(url, attachment)
			return {attachment: data, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const fetchNoteAttachments = createAsyncThunk(
	"noteAttachmentSlice/fetchNoteAttachments",
	async ({note}, {getState, rejectWithValue}) => {
		const state = getState()
		const { workspaceId } = note
		const userId = state.user?.value?.id
		const noteId = note.id

		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment`
			const {data} = await axios.get(url)
			return {attachments: data, noteId, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)


export const fetchSingleNoteAttachments = createAsyncThunk(
	"noteAttachmentSlice/fetchSingleNoteAttachments",
	async ({note}, {getState, rejectWithValue}) => {
		const state = getState()
		const {workspaceId} = note
		const userId = state.user?.value?.id
		const noteId = note.id

		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/singleNoteAttachments`
			const {data} = await axios.get(url)
			return {attachments: data, noteId, workspaceId}
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const fetchNoteAttachmentBreadcrumbs = createAsyncThunk(
	"noteAttachmentSlice/fetchNoteAttachmentBreadcrumbs",
	async ({attachment, force = true, workspaceId}, {getState, rejectWithValue}) => {
		const state = getState()
		const userId = state.user?.value?.id
		const noteId = attachment.noteId

		if (!workspaceId || !userId) {
			console.error("fetchNoteAttachmentBreadcrumbs: invalid workspaceId or userId")
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}
		try {
			const workspace = ensureWorkspace(state.noteAttachmentsSlice, workspaceId)
			const breadcrumbs = workspace.breadcrumbs
			let breadcrumb = breadcrumbs[attachment.id]
			let attachmentNoteRelations = workspace.relations[attachment.id]
			if (!breadcrumb || force) {
				const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment/${attachment.id}/breadcrumb`
				const {data} = await axios.get(url)
				const {relations, superPaths} = data
				breadcrumb = superPaths
				attachmentNoteRelations = relations
			}
			return {breadcrumb, attachmentId: attachment.id, relations: attachmentNoteRelations, workspaceId}
		} catch(error) {
			console.error("Failed to fetch Note Attachment Breadcrumbs: ", error)
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const deleteNoteAttachment = createAsyncThunk(
	"noteAttachmentSlice/deleteNoteAttachment",
	async ({attachment, workspaceId}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const userId = state.user?.value?.id
		const noteId = attachment.noteId
		if (!workspaceId || !userId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}
	
		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment/${attachment.id}`
			const {data} = await axios.delete(url)

			dispatch(fetchUniqueCountsForEachPath({paths: data, workspaceId}))
			dispatch(resetWorkingArticleNoteRelation(workspaceId))
			return {deletedAttachment: attachment, workspaceId }
		} catch(error) {
			return rejectWithValue(error.response?.data?.message || error.message)
		}
	}
)

export const attachNoteToAttachment = createAsyncThunk(
	"noteAttachmentSlice/attachNoteToAttachment",
	async ({attachment, note}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const {workspaceId} = note
		const userId = state.user?.value?.id
		const noteId = note.id
		const attachmentId = attachment.id

		if (!workspaceId || !userId || !noteId || !attachmentId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment/${attachmentId}/noteAttachment`
			await axios.post(url)
			dispatch(fetchUniqueCounts({id: noteId, workspaceId}))
			dispatch(fetchNoteAttachmentBreadcrumbs({attachment, workspaceId}))
			dispatch(setWorkingAttachmentRelation({attachmentId: attachment.id, workspaceId}))
			return {attachment, workspaceId}
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message)
		}
	
	}
)

export const detachNoteFromAttachment = createAsyncThunk(
	"noteAttachmentSlice/detachNoteFromAttachment",
	async ({attachment, note}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const { workspaceId } = note
		const userId = state.user?.value?.id
		const noteId = note.id
		const attachmentId = attachment.id

		if (!workspaceId || !userId || !noteId || !attachmentId) {
			return rejectWithValue("Invalid WorkspaceId or UserId")
		}

		try {
			const url = `/api/workspace/${workspaceId}/user/${userId}/note/${noteId}/attachment/${attachmentId}/noteAttachment`
			await axios.delete(url)
			dispatch(fetchUniqueCounts({id: noteId, workspaceId}))
			dispatch(fetchNoteAttachmentBreadcrumbs({attachment, force: true, workspaceId}))
			dispatch(setWorkingAttachmentRelation({attachmentId: attachment.id, workspaceId}))
			return {note, attachment, workspaceId}
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message)
		}

	}
)

export const createNewTipTapDocument = createAsyncThunk(
	"noteAttachmentSlice/createNewTipTapDocument",
	async ({attachment, note},{rejectWithValue, dispatch}) => {
		const { workspaceId } = note
		const noteId = note.id
		try {
			const {data} = await axios.post(`/api/rte/${workspaceId}/${noteId}/new-doc`, attachment)
			dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
			return {attachment: data, workspaceId}
		} catch (e) {
			console.error("failed to generate TipTap Document: ", e)
			if([519, 401].includes(e.response.status)) {
				return rejectWithValue("Only workspace admins are allowed to create new Document Attachments")
			} else {
				return rejectWithValue(error.response?.data || error.message)
			}
		}
	}
)

export const deleteTipTapDocumentAndNoteAttachment = createAsyncThunk(
	"noteAttachmentSlice/deleteTipTapDocumentAndNoteAttachment",
	async ({attachment, workspaceId}, { rejectWithValue, dispatch}) => {
		const noteId = attachment.noteId
		const attachmentId = attachment.id

		if(!noteId) {
			return rejectWithValue("Invalid Attachment data: missing noteId value")
		}
		try {
			const {data} = await axios.delete(`/api/rte/${workspaceId}/${noteId}/${attachmentId}`)
			dispatch(fetchUniqueCountsForEachPath({paths: data, workspaceId}))
			dispatch(resetWorkingArticleNoteRelation(workspaceId))
			return {deletedAttachment: attachment, workspaceId }
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message)
		}
	}
)

export const editDocumentAttachment = createAsyncThunk(
	"noteAttachmentSlice/editDocumentAttachment",
	async ({attachment, note}, { rejectWithValue}) => {
		const {workspaceId} = note
		const attachmentId = attachment.id

		try {
			const {data} = await axios.patch(`/api/rte/${workspaceId}/${attachmentId}`, attachment)
			return {attachment: data, workspaceId}
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message)
		}
	}
)

/**
 * Redux slice for managing note attachments, including posting, editing,
 * fetching, and deleting attachments. It also handles attachment modal states,
 * validates attachment URLs, and manages associations between notes and attachments.
 * Supports both links and file attachments.
 *
 * @property {Object.<string, Attachment>} noteAttachmentMap - Maps note IDs to their attachments (links and files).
 * @property {?string} selectedNote - ID of the currently selected note or null. Used in the modal only.
 * @property {boolean} isModalOpen - Indicates if the attachment modal is open. Used in the modal only.
 * @property {boolean} isSubmitting - Indicates if an attachment operation is in progress. Used in the modal only.
 * @property {?string} type - Type of attachment being managed (URL or file).
 * @property {?string} error - Error message from attachment operations.
 * @property {Object} noteAttachmentPreview - Preview of the attachment being managed. Used in the modal only.
 * @property {boolean} isUrlValid - Indicates if the URL of the previewed attachment is valid. Used in the modal only.
 * @property {?string} actionType - The action being performed (add, edit).
 * @property {Object.<string, Array>} breadcrumbs - Breadcrumb paths for attachments.
 * @property {Object.<string, Object>} relations - Relationships between attachments and notes.
 */
const initialState = {
	noteAttachmentMap: {},
	selectedNote: null,
	isModalOpen: false,
	isSubmitting: false,
	type: null,
	error: null,
	noteAttachmentPreview: defaultAttachmentObject,
	isUrlValid: true,
	actionType: null,
	breadcrumbs: {},
	relations: {}
}

const withExistingAttachment = (initAttachment, existingAttachment) => ({
	...initAttachment,
	url: existingAttachment.url,
	title: existingAttachment.title,
	description: existingAttachment.description,
	attachmentType: existingAttachment.attachmentType,
	id: existingAttachment.id,
	noteId: existingAttachment.noteId,
})

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const noteAttachmentsSlice = createSlice({
	name: "noteAttachmentsSlice",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		initializeLinkAttachmentModal: (state, action) => {
			const { note, actionType, attachment } = action.payload
			const {workspaceId} = note
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.selectedNote = note
			workspace.isModalOpen = true
			workspace.actionType = actionType
			workspace.type = NOTE_ATTACHMENT_TYPES.url
			const initAttachment = {...defaultAttachmentObject, attachmentType: NOTE_ATTACHMENT_TYPES.url}
			if (attachment) {
				workspace.selectedNote = attachment.note
				workspace.noteAttachmentPreview = withExistingAttachment(initAttachment, attachment)
			} else {
				workspace.noteAttachmentPreview = initAttachment
			}
		},
		initializeDocAttachmentModal: (state, action) => {
			const { note, actionType, attachment } = action.payload
			const {workspaceId} = note
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.selectedNote = note
			workspace.isModalOpen = true
			workspace.actionType = actionType
			workspace.type = NOTE_ATTACHMENT_TYPES.doc
			const initAttachment = {...defaultAttachmentObject, attachmentType: NOTE_ATTACHMENT_TYPES.doc}
			if (attachment) {
				workspace.selectedNote = attachment.note
				workspace.noteAttachmentPreview = withExistingAttachment(initAttachment, attachment)
			} else {
				const date = new Date()
				initAttachment.description = `New Document created on ${formatDateWithYear(date)}`
				workspace.noteAttachmentPreview = initAttachment
			}
		},
		initializeFileAttachmentModal: (state, action) => {
			const { note, actionType, attachment } = action.payload
			const {workspaceId} = note
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.selectedNote = note
			workspace.isModalOpen = true
			workspace.actionType = actionType
			workspace.type = NOTE_ATTACHMENT_TYPES.file
			const initAttachment = {
				...defaultAttachmentObject,
				attachmentType: NOTE_ATTACHMENT_TYPES.file,
				description: "Size: \n File Type: \n File Name: \n",
			}
			if (attachment) {
				workspace.selectedNote = attachment.note
				workspace.noteAttachmentPreview = withExistingAttachment(initAttachment, attachment)
			} else {
				workspace.noteAttachmentPreview = initAttachment
			}
		},
		closeAttachmentModal: (state, action) => {
			noteAttachmentsSlice.caseReducers.resetState(state, action)
		},
		updateNoteAttachmentPreview: (state, action) => {
			const {preview, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.noteAttachmentPreview = preview
			if(workspace.type === NOTE_ATTACHMENT_TYPES.url) {
				workspace.isUrlValid = isURL(preview.url, { require_protocol: false })
			}
		},
		resetState: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.selectedNote = null
			workspace.isModalOpen = false
			workspace.isSubmitting = false
			workspace.type = null
			workspace.error = null
			workspace.isUrlValid = true
			workspace.noteAttachmentPreview = defaultAttachmentObject
			workspace.actionType = null
		},
		clearNoteAttachmentError: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.error = null
		},
		addAttachment: (state, action) => {
			const {attachment, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)

			// Ensure the noteId array is initialized
			if (!workspace.noteAttachmentMap[attachment.noteId]) {
				workspace.noteAttachmentMap[attachment.noteId] = []
			}
		
			// Find the index of the existing attachment, if it exists
			const existingIndex = workspace.noteAttachmentMap[attachment.noteId].findIndex(att => att.id === attachment.id)
		
			if (existingIndex !== -1) {
				// Replace the existing attachment
				workspace.noteAttachmentMap[attachment.noteId][existingIndex] = attachment
			} else {
				// Add the new attachment
				workspace.noteAttachmentMap[attachment.noteId].push(attachment)
			}
			noteAttachmentsSlice.caseReducers.resetState(state, {payload: workspaceId})
		},
		updatedAttachment: (state, action) => {
			const {attachment: updatedAttachment, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// Iterate over each noteId in the noteAttachmentMap
			Object.keys(workspace.noteAttachmentMap).forEach(noteId => {
				// Find the index of the attachment that needs to be updated in each noteId
				const index = workspace.noteAttachmentMap[noteId].findIndex(attachment => attachment.id === updatedAttachment.id)
		
				// Check if the attachment is found in the current noteId
				if (index !== -1) {
					// If found, replace it with the updated one
					workspace.noteAttachmentMap[noteId][index] = updatedAttachment
				}
			})
			// Additionally, ensure the updated attachment is added to its primary noteId if not already present
			if (!workspace.noteAttachmentMap[updatedAttachment.noteId]?.some(attachment => attachment.id === updatedAttachment.id)) {
				if (!workspace.noteAttachmentMap[updatedAttachment.noteId]) {
					workspace.noteAttachmentMap[updatedAttachment.noteId] = []
				}
				workspace.noteAttachmentMap[updatedAttachment.noteId].push(updatedAttachment)
			}
			noteAttachmentsSlice.caseReducers.resetState(state, {payload: workspaceId})
		},
		removeAttachment: (state, action) => {
			const {deletedAttachment, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			// Iterate over each key in noteAttachmentMap
			Object.keys(workspace.noteAttachmentMap).forEach(noteId => {
				// Filter out the deleted attachment for each noteId
				workspace.noteAttachmentMap[noteId] = workspace.noteAttachmentMap[noteId].filter(attachment => attachment.id !== deletedAttachment.id)
			})
			noteAttachmentsSlice.caseReducers.resetState(state, {payload: workspaceId})
		},
		clearAttachmentsFromTab: (state, action) => {
			const {index, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.noteAttachmentMap[index] = initialState.noteAttachmentMap
		},
		detachAttachment: (state, action) => {
			const {note, attachment} = action.payload
			const {workspaceId}	= note
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.noteAttachmentMap[note.id].filter(attachmentObject => attachmentObject.id !== attachment.id)
			noteAttachmentsSlice.caseReducers.resetState(state, {payload: workspaceId})
		},
		setIsSubmittingTrue: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.isSubmitting = true
		},
		setNoteAttachmentError: (state, action) => {
			const {workspaceId, error} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.error = error
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNoteAttachments.fulfilled, (state, action) => {
				const  {attachments, noteId, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.noteAttachmentMap[noteId] = attachments
			})
			// post new non-file attachment handlers
			.addCase(postNoteAttachment.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state,{payload: workspaceId})
			})
			.addCase(postNoteAttachment.fulfilled, 	noteAttachmentsSlice.caseReducers.addAttachment)
			.addCase(postNoteAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error =  "Failed to upload file as an attachment"
				workspace.isSubmitting = false
			})
			// edit note attachment (no file upload) handlers
			.addCase(editNoteAttachment.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state, {payload: workspaceId})
			})
			.addCase(editNoteAttachment.fulfilled, noteAttachmentsSlice.caseReducers.updatedAttachment)
			.addCase(editNoteAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error =  "Failed to edit attachment"
			})
			// post new and upload FILE attachment handlers
			.addCase(postNoteFileAttachment.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state, {payload: workspaceId})
			})
			.addCase(postNoteFileAttachment.fulfilled, noteAttachmentsSlice.caseReducers.addAttachment)
			.addCase(postNoteFileAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error =  "Failed to upload file as an attachment"
				workspace.isSubmitting = false
			})
			// edit attachment and upload new FILE handlers
			.addCase(patchNoteFileAttachment.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state,{payload: workspaceId})
			})
			.addCase(patchNoteFileAttachment.fulfilled, noteAttachmentsSlice.caseReducers.updatedAttachment)
			.addCase(patchNoteFileAttachment.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error =  "Failed to update file attachment"
				workspace.isSubmitting = false
			})
			.addCase(fetchSingleNoteAttachments.fulfilled, (state, action) => {
				const  {attachments, noteId, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.noteAttachmentMap[noteId] = attachments
			})
			.addCase(deleteNoteAttachment.fulfilled, noteAttachmentsSlice.caseReducers.removeAttachment)
			.addCase(fetchNoteAttachmentBreadcrumbs.fulfilled, (state, action) => {
				const { breadcrumb, attachmentId, relations, workspaceId } = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.breadcrumbs[attachmentId] = breadcrumb
				workspace.relations[attachmentId] = relations
			})
			.addCase(attachNoteToAttachment.fulfilled, noteAttachmentsSlice.caseReducers.addAttachment)
			.addCase(attachNoteToAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload
			})
			.addCase(attachArticlesToNote.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				if (action.payload.message) {
					workspace.error = action.payload.message
				} else {
					workspace.error = "Failed to attach articles to note."
				}
			})
			.addCase(attachArticlesToNote.fulfilled, (state, action) => {
				if(action.payload.duplicateAttachmentCount) {
					const {workspaceId} = action.payload
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.error = `Attachment successful, ${action.payload.duplicateAttachmentCount} ${action.payload.duplicateAttachmentCount >= 2 ? "of the selected articles were" : "selected article was"} already attached to the target note.`
				}
			})
			.addCase(detachNoteFromAttachment.fulfilled, noteAttachmentsSlice.caseReducers.detachAttachment)
			.addCase(createNewTipTapDocument.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state, {payload: workspaceId})
			})
			.addCase(createNewTipTapDocument.fulfilled, (state, action) => {
				noteAttachmentsSlice.caseReducers.addAttachment(state, action) // reset slice called here
				window.open(action.payload.attachment.url)
			})
			.addCase(createNewTipTapDocument.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload
				workspace.isSubmitting = false
			})
			.addCase(editDocumentAttachment.pending, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				noteAttachmentsSlice.caseReducers.setIsSubmittingTrue(state, {payload: workspaceId})
			})
			.addCase(editDocumentAttachment.fulfilled, noteAttachmentsSlice.caseReducers.updatedAttachment)
			.addCase(editDocumentAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg.note
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload
				workspace.isSubmitting = false
			})
			.addCase(deleteTipTapDocumentAndNoteAttachment.fulfilled, noteAttachmentsSlice.caseReducers.removeAttachment)
			.addCase(deleteTipTapDocumentAndNoteAttachment.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload
			})
	}
})

export const { 
	initializeLinkAttachmentModal,
	initializeDocAttachmentModal,
	updateNoteAttachmentPreview, 
	clearNoteAttachmentError, 
	addAttachment, 
	updatedAttachment, 
	removeAttachment,
	closeAttachmentModal,
	detachAttachment,
	attachAttachment,
	setNoteAttachmentError,
	clearAttachmentsFromTab,
	initializeFileAttachmentModal,
} = noteAttachmentsSlice.actions

export const selectNoteAttachmentPreview = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.noteAttachmentPreview
export const selectNote = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.selectedNote ?? initialState.selectedNote
export const selectIsModalOpen = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.isModalOpen ?? initialState.isModalOpen
export const selectIsLinkValid = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.isUrlValid ?? initialState.isUrlValid
export const selectActionType = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.actionType ?? initialState.actionType
export const selectNoteAttachmentBreadcrumbs = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.breadcrumbs ?? initialState.breadcrumbs
export const selectNoteAttachmentError = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.error ?? initialState.error
export const selectAttachmentRelations = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.relations ?? initialState.relations
export const selectIsSubmitting = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.isSubmitting ?? initialState.isSubmitting
const selectAttachmentType = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.type ?? initialState.type

export const selectIsNoteAttachmentPreviewValid = (workspaceId) => createSelector(
	[
		(state) => selectNoteAttachmentPreview(state, workspaceId),
		(state) =>selectAttachmentType(state, workspaceId),
		(state) =>selectActionType(state, workspaceId)
	],
	(preview, attachmentType, actionType) => {
		// safeguard against partially loaded app
		if (!preview) return false

		if(attachmentType === NOTE_ATTACHMENT_TYPES.url) {
			return isURL(preview.url, { require_protocol: false }) &&
				preview.title !== "" &&
				preview.description !== ""
		}

		if(attachmentType === NOTE_ATTACHMENT_TYPES.file) {
			if(actionType === "edit-file") {
				// when editing a file attachment, we already have a file in our bucket, so we don't need to confirm that one.
				return (preview.title !== "" && preview.description !== "")
			} else {
				return (preview.fileName && preview.fileName.length > 0) &&
					preview.title !== "" &&
					preview.description !== ""
			}
		}
		// safeguard if attachmentType somehow matches none of them (shouldn't be possible)
		return false
	}
)
export const selectNoteAttachmentMap = (state, workspaceId) => state.noteAttachmentsSlice.byWorkspace[workspaceId]?.noteAttachmentMap ?? initialState.noteAttachmentMap

export default noteAttachmentsSlice.reducer